/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useState } from "react";
import Users from "../../../components/Administrations/AccessControls/Users/Users";
import Authentication from "./Authentication/Authentication";
import Profiles from "../../../components/Administrations/AccessControls/Profiles/Profiles";
import Regions from "./Regions/Regions";
import { Settings } from "../../../components/Administrations/AccessControls/Settings/Settings";
import { useSelector } from "react-redux";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import { useGetAccessControlCountQuery } from "../../../store/api/admin/adminApiSlice";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/tabs";
import { Badge } from "../../../components/ui/badge";

interface ITabItem {
  label: string;
  value: string;
  component: React.ReactNode;
  permissionRequired?: string;
}

const AccessControls: React.FC = () => {
  const permissions = useSelector(selectProfilePermissionsSelector);
  const { data: controlCounts } = useGetAccessControlCountQuery();

  // Provide default values if data is undefined
  const counts = controlCounts || { totalUsers: 0, totalProfiles: 0, totalRegions: 0 };

  const allTabs: ITabItem[] = [
    { label: "Users", value: "users", component: <Users />, permissionRequired: "user_management" },
    { label: "Authentication", value: "authentication", component: <Authentication />, permissionRequired: "user_management" },
    { label: "Profiles", value: "profiles", component: <Profiles />, permissionRequired: "configure_profile" },
    { label: "Regions", value: "regions", component: <Regions /> },
    { label: "Settings", value: "settings", component: <Settings /> },
  ];

  const visibleTabs = allTabs.filter(
    (tab) => !tab.permissionRequired || permissions?.hasOpCodeGroup(tab.permissionRequired)
  );

  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.value || "users");

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <div
        className="sticky top-0 z-10 pl-4 pr-1 backdrop-blur-sm"
        style={{ backgroundColor: "color-mix(in srgb, var(--background) 90%, transparent)" }}
      >
          <div  style={{ borderBottom: "1px solid var(--color-border)" }}>
          <TabsList className="inline-flex h-auto gap-0 rounded-none border-0 bg-transparent p-0 text-muted-foreground shrink-0">
            {visibleTabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm font-medium capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                <div className="flex items-center gap-2">
                  <span>{tab.label}</span>
                  {tab.value === "users" && counts?.totalUsers !== undefined && counts.totalUsers > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs">
                      {counts.totalUsers}
                    </Badge>
                  )}
                  {tab.value === "profiles" && counts?.totalProfiles !== undefined && counts.totalProfiles > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs">
                      {counts.totalProfiles}
                    </Badge>
                  )}
                  {tab.value === "regions" && counts?.totalRegions !== undefined && counts.totalRegions > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs">
                      {counts.totalRegions}
                    </Badge>
                  )}
                </div>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
      </div>
      {visibleTabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value} className="mt-3 pt-0">
          {tab.component}
        </TabsContent>
      ))}
    </Tabs>
  );
};

export default AccessControls;
